import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger, ValidationError } from "@/lib/logger";
import { dateTime } from "@/lib/format";
import { useAuth } from "@/app/auth";
import type { Enums } from "@/types";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type Role = Enums<"app_role">;
export const ROLES: { value: Role; label: string; help: string }[] = [
  { value: "admin", label: "Administrateur", help: "Tout, y compris les utilisateurs et les paramètres" },
  { value: "manager", label: "Manager", help: "Clients, devis, contrats, commandes, factures" },
  { value: "sales", label: "Commercial", help: "Clients, devis, contrats, commandes" },
  { value: "ops", label: "Opérations", help: "Leads, livraisons, contestations" },
  { value: "accountant", label: "Comptable", help: "Factures, paiements, banque, exports" },
  { value: "readonly", label: "Lecture seule", help: "Consultation, aucune modification" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label])) as Record<Role, string>;

interface UserRow {
  id: string; email: string; full_name: string | null; is_active: boolean;
  created_at: string; roles: Role[]; last_sign_in: string | null; confirmed: boolean;
}

function useUsers() {
  return useQuery({
    queryKey: ["users_list"],
    queryFn: async () => { const { data, error } = await supabase.rpc("users_list"); if (error) throw error; return data as unknown as UserRow[]; },
  });
}

function RolePicker({ value, onChange, idPrefix }: { value: Role[]; onChange: (r: Role[]) => void; idPrefix: string }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {ROLES.map((r) => {
        const on = value.includes(r.value);
        return (
          <label key={r.value} htmlFor={`${idPrefix}-${r.value}`} className="flex cursor-pointer items-start gap-2 rounded-xl border border-border/70 p-2.5 text-sm">
            <Switch id={`${idPrefix}-${r.value}`} checked={on} onCheckedChange={(c) => onChange(c ? [...value, r.value] : value.filter((v) => v !== r.value))} />
            <span className="min-w-0"><span className="font-medium">{r.label}</span><span className="block text-xs text-muted-foreground">{r.help}</span></span>
          </label>
        );
      })}
    </div>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<Role[]>(["sales"]);
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: async () => {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) throw new ValidationError("Adresse email invalide.");
      if (roles.length === 0) throw new ValidationError("Choisis au moins un rôle.");
      const { data, error } = await supabase.functions.invoke("user-invite", { body: { email: email.trim(), full_name: fullName.trim(), roles } });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const msg = ctx ? ((await ctx.json().catch(() => null)) as { error?: string } | null)?.error : null;
        throw new ValidationError(msg ?? error.message);
      }
      return data as { email: string };
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["users_list"] });
      toast.success(`Invitation envoyée à ${d.email}`);
      setEmail(""); setFullName(""); setRoles(["sales"]); setError(null); onOpenChange(false);
    },
    onError: (e) => { setError(e instanceof Error ? e.message : "Invitation impossible."); if (!(e instanceof ValidationError)) logger.error("user.invite", e); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Inviter un membre de l'équipe</DialogTitle>
          <DialogDescription>La personne reçoit un lien par email et choisit elle-même son mot de passe. Aucun mot de passe n'est créé ni transmis ici.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); invite.mutate(); }} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="inv-email">Email *</Label><Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@ironclic.com" /></div>
          <div className="space-y-1.5"><Label htmlFor="inv-name">Nom complet</Label><Input id="inv-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Prénom Nom" /></div>
          <div className="space-y-2"><Label>Rôles *</Label><RolePicker value={roles} onChange={setRoles} idPrefix="inv" /></div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end"><Button type="submit" disabled={invite.isPending}>{invite.isPending ? "Envoi…" : "Envoyer l'invitation"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RolesDialog({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [roles, setRoles] = useState<Role[]>(user?.roles ?? []);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      if (roles.length === 0) throw new ValidationError("Choisis au moins un rôle.");
      const { error } = await supabase.rpc("user_set_roles", { _user_id: user!.id, _roles: roles });
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["users_list"] }); toast.success("Rôles mis à jour"); onClose(); },
    onError: (e) => { setError((e as { message?: string }).message ?? "Modification impossible."); if (!(e instanceof ValidationError)) logger.error("user.set_roles", e); },
  });
  if (!user) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rôles de {user.full_name ?? user.email}</DialogTitle>
          <DialogDescription>Un rôle ouvre des écrans ; sans rôle, le compte ne voit rien. Il doit toujours rester un administrateur actif.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RolePicker value={roles} onChange={setRoles} idPrefix={`r-${user.id}`} />
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end"><Button onClick={() => save.mutate()} disabled={save.isPending}>Enregistrer les rôles</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UsersTab() {
  const { user: me, roles: myRoles } = useAuth();
  const isAdmin = myRoles.includes("admin");
  const qc = useQueryClient();
  const { data, error, isLoading } = useUsers();
  const [invite, setInvite] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);

  const setActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => { const { error } = await supabase.rpc("user_set_active", { _user_id: id, _active: active }); if (error) throw error; },
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["users_list"] }); toast.success(v.active ? "Compte réactivé" : "Compte désactivé"); },
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Modification impossible."); logger.error("user.set_active", e); },
  });

  if (!isAdmin) return <p className="text-sm text-muted-foreground">Seuls les administrateurs gèrent les comptes.</p>;
  if (error) return <QueryError error={error} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">Chaque personne a son propre compte et ses rôles. Un compte désactivé ne peut plus rien lire ni écrire, sans être supprimé : l'historique de ce qu'il a fait reste intact.</p>
        <Button onClick={() => setInvite(true)}><UserPlus className="mr-2 h-4 w-4" />Inviter</Button>
      </div>

      <div className="glass overflow-x-auto rounded-2xl p-1" data-testid="users-table">
        <Table>
          <TableHeader><TableRow><TableHead>Personne</TableHead><TableHead>Rôles</TableHead><TableHead>Dernière connexion</TableHead><TableHead>État</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={5} className="text-muted-foreground">Chargement…</TableCell></TableRow> : data?.map((u) => (
              <TableRow key={u.id} className={u.is_active ? "" : "opacity-60"}>
                <TableCell>
                  <div className="font-medium">{u.full_name ?? "—"}{u.id === me?.id && <span className="ml-2 text-xs text-muted-foreground">(toi)</span>}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{u.roles.length ? u.roles.map((r) => <Badge key={r} variant="secondary">{ROLE_LABEL[r] ?? r}</Badge>) : <span className="text-xs text-muted-foreground">aucun rôle</span>}</div></TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.last_sign_in ? dateTime(u.last_sign_in) : u.confirmed ? "jamais" : "invitation en attente"}</TableCell>
                <TableCell>{u.is_active ? <Badge variant="secondary">Actif</Badge> : <Badge variant="destructive">Désactivé</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(u)}><ShieldCheck className="mr-1.5 h-3.5 w-3.5" />Rôles</Button>
                    <Button size="sm" variant={u.is_active ? "outline" : "default"} disabled={u.id === me?.id || setActive.isPending} onClick={() => setActive.mutate({ id: u.id, active: !u.is_active })}>{u.is_active ? "Désactiver" : "Réactiver"}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <InviteDialog open={invite} onOpenChange={setInvite} />
      {editing && <RolesDialog key={editing.id} user={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
